"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import { createPortal } from "react-dom";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, X, Check, DownloadSimple } from "@phosphor-icons/react/dist/ssr";
import ShareProfileCard, {
  SHARE_CARD_WIDTH,
} from "@/components/ShareProfileCard";
import { getPublicProfileUrl } from "@/lib/site-url";
import type { ImageType } from "@/lib/image-utils";

interface ShareButtonProps {
  slug: string;
  name: string;
  /** Public-storage URL of the pro photo. Falls back to initials if missing/blocked. */
  image?: string;
  /** "logo" renders the image contained on a white/gold circle; "photo" covers. */
  imageType?: ImageType;
  /** Pre-formatted professional title (e.g. "Senior Corporate Lawyer") */
  title?: string;
  /** Pre-formatted location label */
  location?: string;
  className?: string;
}


/**
 * Fetch an image URL and return it as a data URL. Used to bypass canvas
 * tainting / CORS quirks when html-to-image rasterizes the share card.
 * Returns null on any failure — caller falls back to initials.
 */
async function imageToDataUrl(src: string): Promise<string | null> {
  try {
    const res = await fetch(src, { mode: "cors", cache: "force-cache" });
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export default function ShareButton({
  slug,
  name,
  image,
  imageType = "photo",
  title,
  location,
  className,
}: ShareButtonProps) {
  const t = useTranslations("shareCard");
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [avatarDataUrl, setAvatarDataUrl] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // QR-encoded URL is always the production hostname so downloaded
  // cards work after sharing, regardless of where they were generated.
  const url = getPublicProfileUrl(slug);

  // Pre-fetch avatar as data URL when modal opens (avoids canvas taint)
  useEffect(() => {
    if (!open || !image || avatarDataUrl) return;
    let cancelled = false;
    imageToDataUrl(image).then((data) => {
      if (!cancelled) setAvatarDataUrl(data);
    });
    return () => { cancelled = true; };
  }, [open, image, avatarDataUrl]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) setOpen(false);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadCard = async () => {
    if (!cardRef.current || downloading) return;
    setDownloading(true);
    try {
      // Wait for fonts so the display serif renders correctly in the export
      if (typeof document !== "undefined" && "fonts" in document) {
        await document.fonts.ready;
      }
      // Dynamic import — only loads when user clicks Download
      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#ffffff",
        width: SHARE_CARD_WIDTH,
        height: cardRef.current.offsetHeight,
      });
      const a = document.createElement("a");
      a.download = `easytiger-${slug}.png`;
      a.href = dataUrl;
      a.click();
    } catch (err) {
      console.error("Share card download failed", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen(true)}
        aria-label={t("ariaLabel", { name })}
        className={className ?? "inline-flex items-center gap-8 rounded-control border border-navy/15 px-13 py-8 text-xs font-semibold uppercase tracking-wider text-navy transition-colors hover:border-navy/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"}
      >
        <QrCode size={15} weight="bold" aria-hidden="true" />
        {t("buttonLabel")}
      </button>

      {open && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("ariaLabel", { name })}
          onClick={handleBackdrop}
          className="fixed inset-0 z-top flex items-center justify-center bg-navy/40 px-21 py-21 backdrop-blur-sm"
        >
          <div
            ref={dialogRef}
            className="relative w-full max-w-[340px] bg-white p-34"
          >
            {/* Close */}
            <button
              onClick={() => setOpen(false)}
              aria-label={t("close")}
              className="absolute right-13 top-13 rounded-control p-5 text-gray-500 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
            >
              <X size={18} weight="bold" aria-hidden="true" />
            </button>

            {/* Heading */}
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-navy/50">
              {t("eyebrow")}
            </p>
            <h2 className="mt-5 truncate font-serif text-xl text-navy">{name}</h2>

            {/* QR preview — same QR that ends up on the downloaded card */}
            <div className="mt-21 flex justify-center rounded-control border border-card-line p-21">
              <QRCodeCanvas
                value={url}
                size={220}
                bgColor="#ffffff"
                fgColor="#040044"
                level="H"
                imageSettings={{
                  src: "/EasyTiger_BadgeOnly_Colour.svg",
                  height: 44,
                  width: 44,
                  excavate: true,
                }}
              />
            </div>

            {/* URL */}
            <p className="mt-13 truncate text-center text-[11px] text-gray-500">
              {url.replace(/^https?:\/\//, "")}
            </p>

            {/* Actions */}
            <div className="mt-21 flex gap-8">
              <button
                onClick={copyLink}
                className="flex flex-1 items-center justify-center gap-8 rounded-control border border-navy/15 py-8 text-xs font-semibold uppercase tracking-wider text-navy transition-colors hover:border-navy/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
              >
                {copied
                  ? <><Check size={13} weight="bold" aria-hidden="true" /> {t("copied")}</>
                  : t("copyLink")
                }
              </button>
              <button
                onClick={downloadCard}
                disabled={downloading}
                aria-label={t("downloadAria")}
                className="inline-flex items-center justify-center rounded-control border border-navy/15 px-13 py-8 text-navy transition-colors hover:border-navy/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold disabled:cursor-wait disabled:opacity-60"
              >
                <DownloadSimple size={15} weight="bold" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Hidden full-size share card — rendered offscreen so html-to-image
              can rasterize it on download. Click events are blocked by the
              modal overlay above. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: -99999,
              top: 0,
              pointerEvents: "none",
              width: SHARE_CARD_WIDTH,
            }}
          >
            <ShareProfileCard
              ref={cardRef}
              name={name}
              title={title}
              location={location}
              url={url}
              avatarDataUrl={avatarDataUrl}
              imageType={imageType}
            />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
